import React, { useState, useEffect, useCallback } from 'react';
import ModalLayer from './ModalLayer';
import { supabase } from '../supabaseClient';
import { getHomeownerToken } from '../utils/storage';
import { useTheme } from '../ThemeContext';
import { getApiUrl } from '../utils/platform';
import { showToast } from './Toast';

// PropertyContacts component - Redesigned with category-based system
// - Homeowners (no Supabase session): fetch, add, edit (own), delete (own) via Netlify Functions
// - Agents (with Supabase session): fetch and edit directly via Supabase client

const PREDEFINED_CATEGORIES = [
  { id: 'electric', label: 'Electric' },
  { id: 'water_sewer', label: 'Water/Sewer' },
  { id: 'waste', label: 'Waste' },
  { id: 'gas', label: 'Gas' },
  { id: 'internet', label: 'Internet' },
  { id: 'tv', label: 'TV' },
  { id: 'hoa', label: 'Homeowners Association' }
];

const PropertyContacts = ({ inviteCodeFromProps, clientId, colors: externalColors, brandColor: externalBrandColor, readOnly = false }) => {
  const { colors: themeColors } = useTheme();
  const colors = externalColors || themeColors;
  const brandColor = externalBrandColor || '#59c4c4';
  
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAgent, setIsAgent] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [customCategory, setCustomCategory] = useState('');
  const [form, setForm] = useState({ utility_name: '', phone: '', website: '' });
  const [editingContact, setEditingContact] = useState(null);
  const [saving, setSaving] = useState(false);

  // Memoized fetch functions to prevent re-renders
  const fetchAgentContacts = useCallback(async () => {
    let query = supabase
      .from('property_contacts')
      .select('*');
    
    // If clientId provided, filter by it
    if (clientId) {
      query = query.eq('client_id', clientId);
    }
    
    const { data, error } = await query.order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching contacts (agent):', error);
      return;
    }

    setContacts(data || []);
  }, [clientId]);

  const homeownerRequestHeaders = async (inviteFromProps) => {
    const headers = { 'Content-Type': 'application/json' };
    const token = await getHomeownerToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
      return headers;
    }
    const urlParams = new URLSearchParams(window.location.search);
    const invite = inviteFromProps || urlParams.get('invite');
    if (invite) headers['x-invite-code'] = invite;
    return headers;
  };

  const fetchHomeownerContacts = useCallback(async () => {
    const headers = await homeownerRequestHeaders(inviteCodeFromProps);

    try {
      const res = await fetch(getApiUrl('/.netlify/functions/client-property-contacts'), { headers });
      const json = await res.json();
      if (json && json.data) setContacts(json.data);
      else setContacts([]);
    } catch (err) {
      console.error('Error fetching homeowner contacts:', err);
    }
  }, [inviteCodeFromProps]);

  useEffect(() => {
    const init = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          setIsAgent(true);
          await fetchAgentContacts();
        } else {
          setIsAgent(false);
          await fetchHomeownerContacts();
        }
      } catch (err) {
        console.error('Error loading contacts:', err);
        setContacts([]);
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [inviteCodeFromProps, fetchAgentContacts, fetchHomeownerContacts]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const closeModal = () => {
    setShowModal(false);
    setSelectedCategory(null);
    setCustomCategory('');
    setForm({ utility_name: '', phone: '', website: '' });
    setEditingContact(null);
  };

  const getHeaders = () => homeownerRequestHeaders(inviteCodeFromProps);

  const categoryToSave = () => (
    selectedCategory === 'custom'
      ? customCategory.trim().toLowerCase().replace(/\s+/g, '_')
      : selectedCategory
  );

  const handleCreate = async () => {
    const categoryToUse = categoryToSave();

    if (!categoryToUse) {
      showToast(selectedCategory === 'custom' ? 'Please enter a category name' : 'Please select a category');
      return;
    }
    
    if (!form.utility_name || !form.phone) {
      showToast('Utility name and phone number are required');
      return;
    }

    const contactData = {
      category: categoryToUse,
      utility_name: form.utility_name,
      phone: form.phone,
      website: form.website || null,
      // Keep old fields for backward compatibility
      name: form.utility_name,
      role: categoryToUse
    };

    setSaving(true);
    try {
      if (isAgent) {
        const insertData = { 
          ...contactData
        };
        
        // Include client_id if provided
        if (clientId) {
          insertData.client_id = clientId;
        }
        
        const { data, error } = await supabase
          .from('property_contacts')
          .insert([insertData])
          .select()
          .single();

        if (error) {
          showToast('Create failed: ' + error.message);
          return;
        }

        setContacts(prev => [data, ...prev]);
      } else {
        const res = await fetch(getApiUrl('/.netlify/functions/client-property-contacts'), {
          method: 'POST',
          headers: await getHeaders(),
          body: JSON.stringify(contactData),
        });
        
        const text = await res.text();
        
        const data = JSON.parse(text);
        if (res.ok) {
          setContacts(prev => [data, ...prev]);
        } else {
          showToast('Create failed: ' + (data.error || data.details || 'Unknown error'));
        }
      }

      closeModal();
    } catch (err) {
      console.error('Full error:', err);
      showToast('Create failed: ' + (err.message || JSON.stringify(err)));
    } finally {
      setSaving(false);
    }
  };

  const openAdd = () => {
    setEditingContact(null);
    setSelectedCategory('');
    setCustomCategory('');
    setForm({ utility_name: '', phone: '', website: '' });
    setShowModal(true);
  };

  const handleEdit = (contact) => {
    const category = contact.category || '';
    const predefined = PREDEFINED_CATEGORIES.some((item) => item.id === category);
    setEditingContact(contact);
    setSelectedCategory(predefined ? category : (category ? 'custom' : ''));
    setCustomCategory(predefined ? '' : category);
    setForm({ 
      utility_name: contact.utility_name || contact.name || '', 
      phone: contact.phone || '', 
      website: contact.website || '' 
    });
    setShowModal(true);
  };

  const handleUpdate = async () => {
    if (!form.utility_name || !form.phone) {
      showToast('Utility name and phone number are required');
      return;
    }

    const categoryToUse = categoryToSave();
    if (!categoryToUse) {
      showToast(selectedCategory === 'custom' ? 'Please enter a category name' : 'Please select a category');
      return;
    }

    const contactData = {
      category: categoryToUse,
      utility_name: form.utility_name,
      phone: form.phone,
      website: form.website || null,
      // Keep old fields for backward compatibility
      name: form.utility_name,
      role: categoryToUse
    };

    setSaving(true);
    try {
      if (isAgent) {
        const { data, error } = await supabase
          .from('property_contacts')
          .update(contactData)
          .eq('id', editingContact.id)
          .select()
          .single();

        if (error) {
          showToast('Update failed: ' + error.message);
          return;
        }

        setContacts(prev => prev.map(c => c.id === editingContact.id ? data : c));
      } else {
        const res = await fetch(getApiUrl(`/.netlify/functions/client-property-contacts?id=${editingContact.id}`), {
          method: 'PUT',
          headers: await getHeaders(),
          body: JSON.stringify(contactData),
        });
        const data = await res.json();
        if (res.ok) {
          setContacts(prev => prev.map(c => c.id === editingContact.id ? data : c));
        } else {
          showToast('Update failed: ' + data.error);
        }
      }

      closeModal();
    } catch (err) {
      showToast('Update failed: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this contact?')) return;

    setSaving(true);
    try {
      if (isAgent) {
        const { error } = await supabase.from('property_contacts').delete().eq('id', id);
        if (error) {
          showToast('Delete failed: ' + error.message);
          return;
        }
        setContacts(prev => prev.filter(c => c.id !== id));
      } else {
        // Homeowner: use backend
        const res = await fetch(getApiUrl(`/.netlify/functions/client-property-contacts?id=${id}`), {
          method: 'DELETE',
          headers: await getHeaders(),
        });
        
        if (res.ok) {
          setContacts(prev => prev.filter(c => c.id !== id));
        } else {
          const text = await res.text();
          const data = JSON.parse(text);
          showToast('Delete failed: ' + (data.error || data.details || 'Unknown error'));
        }
      }
    } catch (err) {
      console.error('Delete error:', err);
      showToast('Delete failed: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const getCategoryLabel = (category) => {
    const predefined = PREDEFINED_CATEGORIES.find(c => c.id === category);
    if (predefined) return predefined.label;
    return category?.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') || 'Other';
  };

  if (loading) return <div style={{padding: '1rem', color: colors?.text || '#000'}}>Loading contacts...</div>;

  const grouped = new Map();
  contacts.forEach((contact) => {
    const key = contact.category || 'other';
    if (!grouped.has(key)) grouped.set(key, []);
    grouped.get(key).push(contact);
  });
  const predefinedOrder = PREDEFINED_CATEGORIES.map((item) => item.id);
  const groups = [...grouped.keys()]
    .sort((a, b) => {
      const ai = predefinedOrder.indexOf(a);
      const bi = predefinedOrder.indexOf(b);
      if (ai === -1 && bi === -1) return getCategoryLabel(a).localeCompare(getCategoryLabel(b));
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    })
    .map((key) => ({
      id: key,
      label: getCategoryLabel(key),
      contacts: grouped.get(key).slice().sort((a, b) => (
        String(a.utility_name || a.name || '').localeCompare(String(b.utility_name || b.name || ''))
      )),
    }));

  const fieldStyle = {
    width: '100%',
    boxSizing: 'border-box',
    padding: '0.75rem',
    borderRadius: '0.5rem',
    border: `2px solid ${colors?.border || '#ddd'}`,
    background: colors?.surfaceAlt || '#f5f5f5',
    color: colors?.text || '#000',
    fontSize: '16px',
    maxWidth: '100%',
  };

  return (
    <div className="provider-list" style={{ minWidth: 0, maxWidth: '100%' }}>
      {!readOnly && (
      <button
        type="button"
        onClick={openAdd}
        style={{
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          minHeight: '48px',
          marginBottom: '1.25rem',
          padding: '0.75rem 1rem',
          border: 'none',
          borderRadius: '0.75rem',
          background: brandColor,
          color: '#083838',
          fontWeight: 700,
          fontSize: '16px',
          cursor: 'pointer',
        }}
      >
        Add
      </button>
      )}

      {groups.length === 0 ? (
        <p style={{ color: colors?.textSecondary || '#666', margin: 0 }}>
          No providers yet.
        </p>
      ) : (
        groups.map((group) => (
          <section key={group.id} style={{ marginBottom: '1.25rem', minWidth: 0, maxWidth: '100%' }}>
            <h3 style={{
              margin: '0 0 0.35rem',
              color: colors?.text || '#000',
              fontSize: '1rem',
              fontWeight: 700,
            }}>
              {group.label}
            </h3>
            {group.contacts.map((contact) => (
              <article
                key={contact.id}
                id={`contact-${contact.id}`}
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '0.75rem',
                  alignItems: 'center',
                  padding: '0.85rem 0',
                  borderBottom: `1px solid ${colors?.border || '#e5e5e5'}`,
                  minWidth: 0,
                  maxWidth: '100%',
                }}
              >
                <div style={{ flex: '1 1 160px', minWidth: 0 }}>
                  <div style={{ color: colors?.text || '#000', fontWeight: 700, overflowWrap: 'anywhere' }}>
                    {contact.utility_name || contact.name}
                  </div>
                  <div style={{ color: colors?.textSecondary || '#666', fontSize: '16px', overflowWrap: 'anywhere' }}>
                    {contact.phone}
                    {contact.website && (
                      <>
                        {' · '}
                        <a
                          href={contact.website.startsWith('http') ? contact.website : `https://${contact.website}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: brandColor, overflowWrap: 'anywhere' }}
                        >
                          {contact.website}
                        </a>
                      </>
                    )}
                  </div>
                </div>
                {!readOnly && (
                <div style={{ display: 'flex', gap: '0.5rem', flex: '0 0 auto' }}>
                  <button
                    type="button"
                    onClick={() => handleEdit(contact)}
                    style={{
                      minHeight: '44px',
                      minWidth: '64px',
                      padding: '0.5rem 0.75rem',
                      background: 'transparent',
                      border: `1px solid ${colors?.border || '#ddd'}`,
                      borderRadius: '0.5rem',
                      color: brandColor,
                      cursor: 'pointer',
                      fontSize: '16px',
                    }}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(contact.id)}
                    style={{
                      minHeight: '44px',
                      minWidth: '72px',
                      padding: '0.5rem 0.75rem',
                      background: 'transparent',
                      border: '1px solid #e74c3c',
                      borderRadius: '0.5rem',
                      color: '#e74c3c',
                      cursor: 'pointer',
                      fontSize: '16px',
                    }}
                  >
                    Delete
                  </button>
                </div>
                )}
              </article>
            ))}
          </section>
        ))
      )}

      {showModal && (
        <ModalLayer>
        <div
          role="dialog"
          aria-label={editingContact ? 'Edit provider' : 'Add provider'}
          className="app-modal-card provider-sheet"
          style={{
            background: colors?.background || colors?.surface || '#fff',
            color: colors?.text || '#000',
            overflowX: 'hidden',
            boxSizing: 'border-box',
            width: '100%',
            maxWidth: '100%',
            paddingTop: '16px',
            paddingRight: '16px',
            paddingBottom: 'calc(16px + env(safe-area-inset-bottom, 0px))',
            paddingLeft: '16px',
          }}
        >
          <form
            onSubmit={(event) => {
              event.preventDefault();
              if (editingContact) handleUpdate();
              else handleCreate();
            }}
            style={{ width: '100%', maxWidth: '560px', minWidth: 0, margin: '0 auto', boxSizing: 'border-box' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', marginBottom: '1rem' }}>
              <h2 style={{ margin: 0, fontSize: '1.35rem', fontWeight: 650 }}>
                {editingContact ? 'Edit provider' : 'Add provider'}
              </h2>
              <button
                type="button"
                onClick={closeModal}
                aria-label="Close"
                style={{
                  minWidth: '44px',
                  minHeight: '44px',
                  border: 'none',
                  background: 'transparent',
                  color: colors?.textSecondary || '#666',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                }}
              >
                ×
              </button>
            </div>

            <label style={{ display: 'block', marginBottom: '1rem' }}>
              <span style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>Category</span>
              <select
                aria-label="Category"
                value={selectedCategory || ''}
                onChange={(event) => setSelectedCategory(event.target.value)}
                style={fieldStyle}
              >
                <option value="">Select a category</option>
                {PREDEFINED_CATEGORIES.map((cat) => (
                  <option key={cat.id} value={cat.id}>{cat.label}</option>
                ))}
                <option value="custom">Custom category</option>
              </select>
            </label>

            {selectedCategory === 'custom' && (
              <label style={{ display: 'block', marginBottom: '1rem' }}>
                <span style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>Category name</span>
                <input
                  type="text"
                  value={customCategory}
                  onChange={(event) => setCustomCategory(event.target.value)}
                  placeholder="Pool service, lawn care"
                  style={fieldStyle}
                />
              </label>
            )}

            <label style={{ display: 'block', marginBottom: '1rem' }}>
              <span style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>Name</span>
              <input
                type="text"
                name="utility_name"
                value={form.utility_name}
                onChange={handleChange}
                placeholder="Duke Energy"
                style={fieldStyle}
              />
            </label>

            <label style={{ display: 'block', marginBottom: '1rem' }}>
              <span style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>Phone</span>
              <input
                type="tel"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                placeholder="(800) 123-4567"
                style={fieldStyle}
              />
            </label>

            <label style={{ display: 'block', marginBottom: '1.25rem' }}>
              <span style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>Website</span>
              <input
                type="url"
                name="website"
                value={form.website}
                onChange={handleChange}
                placeholder="www.example.com"
                style={fieldStyle}
              />
            </label>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
              <button
                type="button"
                onClick={closeModal}
                style={{
                  flex: '1 1 120px',
                  minHeight: '48px',
                  padding: '0.75rem',
                  borderRadius: '0.5rem',
                  border: `2px solid ${colors?.border || '#ddd'}`,
                  background: 'transparent',
                  color: colors?.text || '#000',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '16px',
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                style={{
                  flex: '1 1 120px',
                  minHeight: '48px',
                  padding: '0.75rem',
                  borderRadius: '0.5rem',
                  border: 'none',
                  background: saving ? '#ccc' : brandColor,
                  color: '#083838',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  fontWeight: 700,
                  fontSize: '16px',
                }}
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </form>
        </div>
        </ModalLayer>
      )}
    </div>
  );
};

export default PropertyContacts;
